'use client';

import { useIsomorphicLayoutEffect } from '@magicschool/spellbook/hooks/useIsomorphicLayoutEffect';
import { useLatestRef } from '@magicschool/spellbook/hooks/useLatestRef';
import {
  contentInlineSize,
  reconcileObservedItems,
  SUBPIXEL_TOLERANCE,
  toPixels,
  unobserveMissingItems,
} from '@magicschool/spellbook/lib/overflowMeasurement';
import { createContext, type ReactNode, type RefObject, useCallback, useContext, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * Marks a row child as a group, and carries its position. A collapsed group leaves the row, so DOM
 * order stops matching position and the attribute has to say which group a measurement belongs to.
 */
export const TOOLBAR_GROUP_ID_ATTRIBUTE = 'data-toolbar-group';

const DIVIDER_SELECTOR = '[data-spellbook-name="toolbar-divider"]';
const GROUP_SELECTOR = `[${TOOLBAR_GROUP_ID_ATTRIBUTE}]`;
const OVERFLOW_SELECTOR = '[data-spellbook-name="toolbar-overflow"]';

/** Width of a `Toolbar.Divider` (`w-px`) until one has been measured. */
const FALLBACK_DIVIDER_WIDTH = 1;

const NO_COLLAPSED_GROUPS: ReadonlySet<string> = new Set();

/** What `Toolbar` knows about one `Toolbar.Group` while deciding which groups fit. */
export type ToolbarGroupDescriptor = {
  /** The group's position in the row, rendered as `data-toolbar-group` and used to cache its width. */
  readonly id: string;
  /** Whether the group may move into the overflow panel at all. */
  readonly collapsible: boolean;
  /** Highest collapses first; equal values collapse right to left. */
  readonly collapsePriority: number;
  /** Whether one of the group's controls is on, so the overflow trigger can flag it while hidden. */
  readonly active: boolean;
};

export type ToolbarOverflowContextValue = {
  /** The collapsed groups, divider-separated, or `null` while every group fits in the row. */
  readonly collapsedGroups: ReactNode;
  /** Whether any collapsed group declared itself `active`. */
  readonly hasActiveCollapsed: boolean;
  /** The toolbar row, so the overflow panel can cap its width to the toolbar's. */
  readonly rowRef: RefObject<HTMLDivElement | null>;
};

export const ToolbarOverflowContext = createContext<ToolbarOverflowContextValue | null>(null);

export function useToolbarOverflowContext(): ToolbarOverflowContextValue {
  const context = useContext(ToolbarOverflowContext);

  if (!context) {
    throw new Error('Toolbar.Overflow can only render inside a Toolbar, which supplies the groups it has collapsed.');
  }

  return context;
}

type RowMeasurements = {
  /** The row's content width, its own padding excluded. */
  available: number;
  gap: number;
  groupWidths: Map<string, number>;
  /** Combined width of row children that are neither a group, an automatic divider, nor the overflow trigger. */
  staticWidth: number;
  staticCount: number;
  /** `null` when the row currently renders no divider / no trigger to measure. */
  dividerWidth: number | null;
  triggerWidth: number | null;
};

function isOutOfFlow(element: Element): boolean {
  const { position, display } = getComputedStyle(element);
  return position === 'absolute' || position === 'fixed' || display === 'none';
}

/**
 * Reads the row's own width plus the natural width of each of its children. Groups are keyed by
 * identity; the divider and trigger are kept apart from the static total because how many of them
 * render depends on how many groups end up visible.
 */
function measureRow(row: HTMLElement, withDividers: boolean): RowMeasurements {
  const style = getComputedStyle(row);
  const measurements: RowMeasurements = {
    available: contentInlineSize(row, style),
    gap: toPixels(style.columnGap),
    groupWidths: new Map(),
    staticWidth: 0,
    staticCount: 0,
    dividerWidth: null,
    triggerWidth: null,
  };

  for (const child of Array.from(row.children)) {
    // Out-of-flow children are not flex items, so they take neither width nor a gap. Spellbook's
    // `Tooltip` puts one in the row per control: an `sr-only` div holding the trigger's name.
    if (isOutOfFlow(child)) continue;

    const width = child.getBoundingClientRect().width;
    const groupId = child.getAttribute(TOOLBAR_GROUP_ID_ATTRIBUTE);

    if (groupId !== null) {
      measurements.groupWidths.set(groupId, width);
    } else if (child.matches(OVERFLOW_SELECTOR)) {
      measurements.triggerWidth = width;
    } else if (withDividers && child.matches(DIVIDER_SELECTOR)) {
      measurements.dividerWidth = width;
    } else {
      measurements.staticWidth += width;
      measurements.staticCount += 1;
    }
  }

  return measurements;
}

type RowCostInput = {
  visibleGroupWidths: number[];
  hasCollapsed: boolean;
  withDividers: boolean;
  dividerWidth: number;
  triggerWidth: number;
  measurements: RowMeasurements;
};

/** Width the row would occupy for one candidate set of visible groups, gaps and dividers included. */
function rowCost({ visibleGroupWidths, hasCollapsed, withDividers, dividerWidth, triggerWidth, measurements }: RowCostInput): number {
  const dividerCount = withDividers ? Math.max(0, visibleGroupWidths.length - 1) : 0;
  const itemCount = visibleGroupWidths.length + dividerCount + (hasCollapsed ? 1 : 0) + measurements.staticCount;
  const contentWidth =
    visibleGroupWidths.reduce((total, groupWidth) => total + groupWidth, 0) +
    dividerCount * dividerWidth +
    (hasCollapsed ? triggerWidth : 0) +
    measurements.staticWidth;

  return contentWidth + Math.max(0, itemCount - 1) * measurements.gap;
}

/** Identities of the collapsible groups in the order they give up their place in the row. */
function resolveCollapseOrder(groups: readonly ToolbarGroupDescriptor[]): string[] {
  return groups
    .map((group, index) => ({ group, index }))
    .filter(({ group }) => group.collapsible)
    .sort((a, b) => b.group.collapsePriority - a.group.collapsePriority || b.index - a.index)
    .map(({ group }) => group.id);
}

type ResolveCollapsedInput = {
  groups: readonly ToolbarGroupDescriptor[];
  groupWidths: ReadonlyMap<string, number>;
  measurements: RowMeasurements;
  withDividers: boolean;
  dividerWidth: number;
  triggerWidth: number;
};

/** Collapses groups one at a time, in collapse order, until what is left fits the row. */
function resolveCollapsedIds({
  groups,
  groupWidths,
  measurements,
  withDividers,
  dividerWidth,
  triggerWidth,
}: ResolveCollapsedInput): ReadonlySet<string> {
  const collapsed = new Set<string>();

  const fits = () => {
    const cost = rowCost({
      visibleGroupWidths: groups.filter((group) => !collapsed.has(group.id)).map((group) => groupWidths.get(group.id) ?? 0),
      hasCollapsed: collapsed.size > 0,
      withDividers,
      dividerWidth,
      triggerWidth,
      measurements,
    });

    return cost <= measurements.available + SUBPIXEL_TOLERANCE;
  };

  for (const id of resolveCollapseOrder(groups)) {
    if (fits()) break;
    collapsed.add(id);
  }

  return collapsed;
}

function isSameIdSet(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  return left.size === right.size && Array.from(left).every((id) => right.has(id));
}

export type UseToolbarOverflowOptions = {
  /** Every `Toolbar.Group`, in JSX order. */
  groups: readonly ToolbarGroupDescriptor[];
  /** Only a horizontal toolbar measures; a vertical one keeps every group in its column. */
  enabled: boolean;
  withDividers: boolean;
};

export type UseToolbarOverflowResult = {
  /** Attach to the toolbar row: it is both the measured container and the source of the widths. */
  rowRef: RefObject<HTMLDivElement | null>;
  collapsedIds: ReadonlySet<string>;
};

/**
 * Decides which `Toolbar.Group`s fit the row and which belong in the overflow panel.
 *
 * Measurement runs before paint, so the first pass renders every group, measures it, and hides
 * whatever does not fit without the row ever painting too wide. A `ResizeObserver` watches the row
 * and each visible group, so both a narrower container and a group that grew re-run the decision.
 *
 * Widths are cached per group position because a collapsed group is not in the row to measure. A
 * group whose content changes while it is collapsed therefore keeps its previous width until it is
 * visible again; a group that has never been measured forces one pass with everything visible.
 */
export function useToolbarOverflow({ groups, enabled, withDividers }: UseToolbarOverflowOptions): UseToolbarOverflowResult {
  const rowRef = useRef<HTMLDivElement>(null);
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(NO_COLLAPSED_GROUPS);
  const groupWidthsRef = useRef(new Map<string, number>());
  const dividerWidthRef = useRef(FALLBACK_DIVIDER_WIDTH);
  const triggerWidthRef = useRef(0);
  const latestRef = useLatestRef({ groups, enabled, withDividers, collapsedIds });

  const synchronize = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;

    const { groups, enabled, withDividers, collapsedIds } = latestRef.current;
    const showEveryGroup = () => {
      if (collapsedIds.size > 0) setCollapsedIds(NO_COLLAPSED_GROUPS);
    };

    if (!enabled) {
      showEveryGroup();
      return;
    }

    const measurements = measureRow(row, withDividers);

    // A row with no width has not been laid out (a hidden tab, a panel that has not opened). Its
    // children measure zero too, so this has to return before the caches below learn those zeroes.
    if (measurements.available <= 0) return;

    if (measurements.dividerWidth !== null) dividerWidthRef.current = measurements.dividerWidth;
    if (measurements.triggerWidth !== null) triggerWidthRef.current = measurements.triggerWidth;

    const groupWidths = groupWidthsRef.current;
    for (const [id, width] of measurements.groupWidths) {
      groupWidths.set(id, width);
    }
    for (const id of Array.from(groupWidths.keys())) {
      if (!groups.some((group) => group.id === id)) groupWidths.delete(id);
    }

    // A group added while others were collapsed has no cached width, so give it one pass in the row.
    if (groups.some((group) => !groupWidths.has(group.id))) {
      showEveryGroup();
      return;
    }

    const next = resolveCollapsedIds({
      groups,
      groupWidths,
      measurements,
      withDividers,
      dividerWidth: dividerWidthRef.current,
      triggerWidth: triggerWidthRef.current,
    });

    if (!isSameIdSet(next, collapsedIds)) setCollapsedIds(next);
  }, [latestRef]);

  // Re-measures after every render, which is how the trigger's own width reaches the next
  // decision: it only exists in the DOM once something has collapsed.
  useIsomorphicLayoutEffect(() => {
    synchronize();
  });

  const observerRef = useRef<ResizeObserver | null>(null);
  const observedGroupsRef = useRef(new Set<Element>());

  useIsomorphicLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || !enabled) return;

    const observedGroups = observedGroupsRef.current;
    // Observers fire between layout and paint; committing synchronously keeps the resized frame
    // from painting the old collapse set, clipped, before the new one lands a frame later. A group
    // that commit removed is dropped at once: left observed, it reports a 0 size mid-delivery.
    const observer = new ResizeObserver(() => {
      flushSync(synchronize);
      unobserveMissingItems(observer, groupsInRow(row), observedGroups);
    });
    observer.observe(row);
    reconcileObservedItems(observer, groupsInRow(row), observedGroups);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observedGroups.clear();
      observerRef.current = null;
    };
  }, [synchronize, enabled]);

  const groupKey = groups.map((group) => group.id).join('\n');
  const collapsedKey = Array.from(collapsedIds).join('\n');

  // A frame later: this can run inside the observer's own synchronous commit, and a target observed
  // mid-delivery is skipped and reported as a ResizeObserver loop error.
  useIsomorphicLayoutEffect(() => {
    const observer = observerRef.current;
    const row = rowRef.current;
    if (!observer || !row) return;

    const frame = requestAnimationFrame(() => reconcileObservedItems(observer, groupsInRow(row), observedGroupsRef.current));
    return () => cancelAnimationFrame(frame);
  }, [groupKey, collapsedKey]);

  return { rowRef, collapsedIds };
}

function groupsInRow(row: HTMLElement): Element[] {
  return Array.from(row.children).filter((child) => child.matches(GROUP_SELECTOR));
}
